import { useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { useNavigate } from 'react-router-dom';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { ColumnMenu } from '../components/columnMenu';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { ActionCell } from '../components/grid/actions-cell';
import { LinkCell } from '../components/grid';
import { PatchSupportTicketAsync } from '../services/support-tickets';
import { SupportTicket } from '../types/support-ticket';
import { formatDateToDDMMYYYY } from '../utils/date';
import { useToast } from '../contexts/toast-context';
import { useGetSupportTickets } from '../hooks/services/use-support-ticket';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'id', dir: 'desc' }],
  ...DefaultGridSettings.initialDataState
};

const SupportTicketsGrid = () => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>({ ...initialDataState });

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetSupportTickets(
    toDataSourceRequestString(dataState)
  );

  const handleClosedTicket = async (dataItem: SupportTicket) => {
    let newData = { ...dataItem };
    newData.status = 'Closed';
    try {
      await PatchSupportTicketAsync(dataItem.id as number, newData);

      mutate();
      onToastOpen({
        title: 'Support Tickets',
        body: <div>Support ticket status changed.</div>,
        style: 'success'
      });
    } catch (err) {
      onToastOpen({
        title: 'Support Tickets',
        body: <div>Failed to change support ticket status.</div>,
        style: 'error'
      });
    }
  };

  const gridTicketDate = (props: GridCellProps) => (
    <td className={'k-table-td'}>
      <span>{formatDateToDDMMYYYY(props.dataItem.createdAt, true)}</span>
    </td>
  );

  const gridTitle = (props: GridCellProps) => (
    <LinkCell
      to={`/administration/support_tickets/${props.dataItem['id']}`}
      field={'createdAt'}
      valueCell={props.dataItem.title}
      {...props}
    />
  );

  const generateTicketNumber = (idTicket: number) => {
    let numberStr = idTicket.toString();

    // Determines the number of digits that need to be added
    let numZeros = Math.max(0, 5 - numberStr.length);

    // Generates a string by adding leading 0 (if needed)
    let resultStr = '0'.repeat(numZeros) + numberStr;

    return resultStr;
  };

  const gridTicketNumber = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {generateTicketNumber(props.dataItem.id)}
      </td>
    );
  };

  const gridActionsColumn = (props: GridCellProps) => {
    return (
      <div>
        <ActionCell
          title={'Edit'}
          icon={'edit'}
          themeColor={'primary'}
          className={'fs-2'}
          onClick={() =>
            navigate(`/administration/support_tickets/${props.dataItem.id}`)
          }
        />
        <ActionCell
          isDisable={props.dataItem.status === 'Closed'}
          title={'Mark as closed'}
          icon={'check_circle'}
          themeColor={'success'}
          className={'fs-2'}
          onClick={() => handleClosedTicket(props.dataItem as SupportTicket)}
        />
      </div>
    );
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  return (
    <>
      <Toolbar title={'Support Tickets'}></Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search tickets'}
          columnsToSearch={['title', 'requestedBy']}
          showCardMode={false}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`support-tickets.xlsx`}>
          <Grid
            id={'grid-support-tickets'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'title'}
              title={'Ticket Title'}
              cells={{ data: gridTitle }}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'createdAt'}
              title={'Ticket Date'}
              format={'{0:g}'}
              cells={{ data: gridTicketDate }}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'ticketNumber'}
              title={'Ticket Number'}
              cells={{ data: gridTicketNumber }}
              columnMenu={ColumnMenu}
            />

            <Column
              field={'requestedBy'}
              title={'Requester'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'status'}
              title={'Status'}
              filter={'text'}
              columnMenu={ColumnMenu}
            />
            <Column
              title={'Actions'}
              cells={{ data: gridActionsColumn }}
              width={110}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>
    </>
  );
};
export default SupportTicketsGrid;
